import RouteTemplate from 'ember-route-template'
import BaseRouteTemplate from 'customer-flows/templates/base-route-template'
import TwoColumn from 'customer-flows/components/layout-kit/two-column'
import HeroContainer from 'customer-flows/components/flow-kit/hero-container'
import StepContainer from 'customer-flows/components/flow-kit/step-container'
import StaticFooter from 'customer-flows/components/booking/static-footer'
import StickyFooter from 'customer-flows/components/booking/sticky-footer'
import CartSummary from 'customer-flows/components/booking/cart-summary'
import CartSummarySkeleton from 'customer-flows/components/booking/cart-summary-skeleton'
import ReviewCards from 'customer-flows/components/booking/review-cards'
import t from 'ember-intl/helpers/t'
import didInsert from '@ember/render-modifiers/modifiers/did-insert'
import { not } from 'ember-truth-helpers'
import { action } from '@ember/object'
import { service } from '@ember/service'
import { context } from 'customer-flows/utils/core'
import type PosthogService from 'customer-flows/services/trackers/posthog'

class BookingStartTemplate extends BaseRouteTemplate<BookingStartPage> {
  @service quoteBuilder: QuoteBuilder
  @service('trackers/posthog') posthog: PosthogService
  @context('quote') quote: QuoteStore['quote']
  @context('quote') hasQuote: QuoteStore['hasQuote']
  @context('availability') availabilityLoading: AvailabilityStore['availabilityLoading']
  @context('availability') validTimeslots: AvailabilityStore['validTimeslots']
  @context('availability') hasValidAvailability: AvailabilityStore['hasValidAvailability']
  @context('tickets') hasValidTicketSelection: TicketsStore['hasValidTicketSelection']
  @context('tickets') hasMetMinimumTicketRequirement: TicketsStore['hasMetMinimumTicketRequirement']
  @context('tickets') minRequiredTickets: TicketsStore['minRequiredTickets']
  @context('activity') qualifiesForReviews: ActivityStore['qualifiesForReviews']
  @context('activity') reviewCount: ActivityStore['reviewCount']
  @context('activity') averageRating: ActivityStore['averageRating']
  @context('activity') topReviews: ActivityStore['topReviews']

  get allStepsValid() {
    return this.flowManager.allStepsValid
  }

  get showReviews(): boolean {
    return this.qualifiesForReviews && this.posthog.isVariantFor('reviews', 'test') === true
  }

  get continueButtonDisabled(): boolean {
    const missingQuote = !this.quote.value
    const invalidAvailability = !this.hasValidAvailability || this.availabilityLoading
    const invalidTicketSelection = !this.hasValidTicketSelection || !this.hasMetMinimumTicketRequirement

    return !this.allStepsValid || missingQuote || invalidAvailability || invalidTicketSelection
  }

  get showStickyFooter() {
    return this.allStepsValid && this.hasQuote
  }

  get showCartSummary() {
    return this.hasQuote
  }

  get showCartSummarySkeleton() {
    return this.quote.loading && !this.hasQuote
  }

  get showCartSummarySpinner() {
    return this.quote.loading && this.hasQuote
  }

  @action
  setup() {
    this.quoteBuilder.include('activityId')
  }

  @action
  async onBookClicked() {
    if (!this.continueButtonDisabled) {
      this.flowManager.forward()
    }
  }

  <template>
    <TwoColumn data-testid='booking-flow' @fillHeight={{true}} {{didInsert this.setup}}>
      <:leftColumn>
        <HeroContainer />

        {{#if this.showReviews}}
          <div class='hidden @md/flow:block'>
            <ReviewCards @reviews={{this.topReviews}} @count={{this.reviewCount}} @avgRating={{this.averageRating}} />
          </div>
        {{/if}}
      </:leftColumn>

      <:rightColumn>
        <div class='flex flex-col gap-4'>
          <StepContainer />

          {{#if this.showReviews}}
            <div class='@md/flow:hidden'>
              <ReviewCards @reviews={{this.topReviews}} @count={{this.reviewCount}} @avgRating={{this.averageRating}} />
            </div>
          {{/if}}

          {{#if this.allStepsValid}}
            <il-section
              header={{t 'booking.cart-summary.header'}}
              class='@sm/flow:il-section:rounded-3 hidden @md/flow:block'
            >
              {{#if this.showCartSummarySpinner}}
                <div slot='header-right'>
                  <il-loader ignore-theme tiny></il-loader>
                </div>
              {{/if}}

              <div slot='content'>
                {{#if this.showCartSummarySkeleton}}
                  <CartSummarySkeleton />
                {{else}}
                  <CartSummary @showTaxesAndFees={{true}} />
                {{/if}}
              </div>
            </il-section>
          {{/if}}
        </div>
      </:rightColumn>

      <:staticFooter>
        <StaticFooter @text={{t 'booking.footer.text' htmlSafe=true}} />
      </:staticFooter>

      <:stickyFooter>
        <StickyFooter @hidden={{not this.showStickyFooter}}>
          {{#if this.showCartSummary}}
            <CartSummary class='@md/flow:hidden' @collapsible={{true}} @isMobile={{true}} />
          {{/if}}
          <il-tooltip
            fill
            disableContentHovering={{this.hasMetMinimumTicketRequirement}}
            tooltipText={{t 'booking.min-tickets-required.message' count=this.minRequiredTickets}}
          >
            <il-button
              fill
              {{! @glint-ignore: wait until we can't declare custom elements in glint }}
              onClick={{this.onBookClicked}}
              disabled={{this.continueButtonDisabled}}
              is-loading={{this.availabilityLoading}}
              data-testid='date-first-rentals-flow__continue-cta'
            >
              {{t 'booking.cta.continue-booking'}}
            </il-button>
          </il-tooltip>
        </StickyFooter>
      </:stickyFooter>
    </TwoColumn>
  </template>
}

export default RouteTemplate(BookingStartTemplate)
